<template>
  <div class="vn-container" @click="handleInteraction">
    <!-- 背景层 -->
    <div class="vn-background">
      <div class="bg-img" :style="{ backgroundImage: `url(${currentBg})` }"></div>
      <div class="moon-light"></div>
      <div class="screen-glow"></div>
      <div class="vignette"></div>
      <div class="scanlines"></div>
    </div>

    <!-- 人物立绘 -->
    <div class="characters">
      <transition name="fade-char">
        <div v-if="charLeft.show" class="char char-left" :class="{ 'char-active': charLeft.active }">
          <img :src="charLeft.src" alt="">
        </div>
      </transition>

      <transition name="fade-char">
        <div v-if="charRight.show" class="char char-right" :class="{ 'char-active': charRight.active }">
          <img :src="charRight.src" alt="">
        </div>
      </transition>
    </div>

    <!-- 音频初始化遮罩 -->
    <transition name="fade">
      <div v-if="!isAudioInitialized" class="audio-gate">
        <div class="gate-content">
          <span class="icon">🔊</span>
          <p>点击屏幕以初始化神经链接与音频系统</p>
        </div>
      </div>
    </transition>

    <!-- 主内容 -->
    <div class="vn-main">
      <!-- 章节标题 -->
      <div class="chapter-title">
        <span class="line"></span>
        {{ currentChapterTitle }}
        <span class="line"></span>
      </div>

      <!-- 场景提示 -->
      <transition name="slide-up">
        <div :key="currentText.hint" class="scene-hint">
          {{ currentText.hint }}
        </div>
      </transition>

      <!-- 对话面板 -->
      <div class="dialogue-wrapper" v-if="currentStep >= 0">
        <div class="dialogue-box" :class="{ shake: currentText.shake }">
          <div class="char-name" v-if="currentText.speaker">{{ currentText.speaker }}</div>
          <div class="text-content">
            {{ displayedText }}<span class="cursor" v-if="isTyping">_</span>
          </div>
          <div class="next-indicator" v-if="!isTyping && !showOptions"></div>
        </div>
      </div>

      <!-- 选项分支 -->
      <transition name="fade-options">
        <div v-if="showOptions" class="options-box">
          <button v-for="(opt, i) in currentOptions" :key="i" class="option-btn" @click.stop="selectOption(opt.next)">
            {{ opt.text }}
          </button>
        </div>
      </transition>
    </div>

    <!-- 顶部控制栏 -->
    <div class="top-controls">
      <span @click.stop="toggleAuto">{{ autoPlay ? '取消自动' : '自动播放' }}</span>
      <span @click.stop="quickSave">快速存档</span>
      <span @click.stop="quickLoad">快速读档</span>
    </div>

    <!-- 底部UI -->
    <div class="ui-overlay">
      <div class="audio-status">AUDIO_SYNC: {{ isAudioInitialized ? 'STABLE' : 'WAITING' }}</div>
      <div class="controls">
        <span @click.stop="toggleMute">{{ isMuted ? 'UNMUTE' : 'MUTE' }}</span>
        <span>LOG</span>
        <span>SAVE</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// ==============================================
// 【完整原文剧情 · 100% 还原】
// ==============================================
const scriptData = {
  chapters: [
    {
      title: "第一章 · 聚窟洲的初遇",
      bg: "https://picsum.photos/id/1039/1600/900", // 深夜宿舍氛围背景
      script: [
        {
          hint: "杭州 · 宿舍",
          speaker: "旁白",
          content: "宿舍的空调坏了第三天，徐荣兵的后背黏在电竞椅上，像块融化的太妃糖。显示器蓝光刺得他眼球发胀，《永结无间》里的迦南正蹲在聚窟洲断墙后，匕首的寒光与窗外真实的月光重叠在一起。",
          sfx: 'ambient_start',
          charLeft: { show: true, src: 'https://picsum.photos/id/1012/400/800', active: true },
          charRight: { show: false },
        },
        {
          hint: "突发状况",
          speaker: "系统",
          content: "突然耳机里炸开清脆的女声：“喂喂？能听见吗？”吓得他碰翻了可乐罐，褐色液体在键盘F键上积成微型沼泽。",
          sfx: 'spill',
          shake: true,
          charLeft: { show: true, active: false },
          charRight: { show: false },
        },
        {
          hint: "糕手芸芸",
          speaker: "芸芸 (๑•̀ㅂ•́)و✧",
          content: "“我是芸芸。”屏幕左下角跳动的ID（糕手芸芸）后面跟着颜文字，她操作的玉玲珑正用长剑戳着地面画圈，“第一次匹配到会说话的活人诶。”",
          sfx: 'chat',
          charLeft: { show: true, active: false },
          charRight: { show: true, src: 'https://picsum.photos/id/1027/400/800', active: true },
        },
        {
          hint: "徐荣兵的回应",
          speaker: "徐荣兵",
          content: "徐荣兵闻到自己T恤上的汗酸味，突然庆幸对方闻不到。他喉结滚动三次才憋出回应：“...嗯。”",
          sfx: 'type',
          charLeft: { show: true, active: true },
          charRight: { show: true, active: false },
        },
        {
          hint: "反杀瞬间",
          speaker: "芸芸",
          content: "当敌方崔三娘从房梁荡下来时，徐荣兵的手比大脑更快。迦南的链刃绞住对方脖颈的瞬间，芸芸的玉玲珑突然闪现到他身前，长剑格挡的金属铮鸣震得耳机发颤。“漂亮的反杀！”她欢呼时带起细微的电流杂音，像盛夏的静电。",
          sfx: 'glitch',
          charLeft: { show: true, active: false },
          charRight: { show: true, active: true },
        },
        {
          hint: "情绪变化",
          speaker: "旁白",
          content: "徐荣兵发现自己的嘴角莫名其妙翘了起来，这比他预想中多用了0.7秒去控制面部肌肉。",
          charLeft: { show: true, active: true },
          charRight: { show: true, active: false },
        },
        {
          hint: "胜利动画",
          speaker: "旁白",
          content: "胜利动画里，两个角色站在燃烧的篝火旁。芸芸突然让玉玲珑做出游戏里最昂贵的双人动作——“比心”，白衣女侠向迦南伸出邀请的手势。",
          bg: "https://picsum.photos/id/1036/1600/900",
          charLeft: { show: true, active: false },
          charRight: { show: true, active: true },
        },
        {
          hint: "心动时刻",
          speaker: "旁白",
          content: "徐荣兵手忙脚乱按错三个键才接住，显示器映出他发红的耳尖。",
          charLeft: { show: true, active: true },
          charRight: { show: true, active: false },
        },
        {
          hint: "约定",
          speaker: "芸芸",
          content: "“明天同样时间？”退出前她问。徐荣兵盯着好友列表里新增的粉色爱心图标，听见窗外蝉鸣突然拔高了一个八度。他舔到上唇的汗珠，咸得像海，而杭州分明是内陆城市。",
          sfx: 'cicada',
          charLeft: { show: true, active: false },
          charRight: { show: true, active: true },
        },
        {
          hint: "第一章 完",
          speaker: "系统",
          content: "（第一章 完）",
          end: true
        }
      ]
    }
  ]
}

// ==============================================
// 状态管理
// ==============================================
const currentChapter = ref(0)
const currentStep = ref(0)
const displayedText = ref('')
const isTyping = ref(false)
const isAudioInitialized = ref(false)
const isMuted = ref(false)
const autoPlay = ref(false)
let autoTimer = null

const currentBg = ref('')
const charLeft = ref({ show: false, src: '', active: false })
const charRight = ref({ show: false, src: '', active: false })

const showOptions = ref(false)
const currentOptions = ref([])

const currentChapterTitle = computed(() => scriptData.chapters[currentChapter.value].title)
const currentScript = computed(() => scriptData.chapters[currentChapter.value].script)
const currentText = computed(() => currentScript.value[currentStep.value])

// ==============================================
// 音频
// ==============================================
const sounds = {
  click: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3',
  type: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3',
  spill: 'https://assets.mixkit.co/active_storage/sfx/1210/1210-preview.mp3',
  cicada: 'https://assets.mixkit.co/active_storage/sfx/1234/1234-preview.mp3'
}

const playSFX = (type) => {
  if (isMuted.value || !isAudioInitialized.value) return
  const audio = new Audio(sounds[type] || sounds.click)
  audio.volume = type === 'type' ? 0.15 : 0.3
  audio.play().catch(() => { })
}

// ==============================================
// 加载场景
// ==============================================
function loadScene() {
  const text = currentText.value
  if (!text) return

  if (text.bg) currentBg.value = text.bg
  else currentBg.value = scriptData.chapters[currentChapter.value].bg

  if (text.charLeft) charLeft.value = { ...text.charLeft }
  if (text.charRight) charRight.value = { ...text.charRight }

  if (text.options) {
    showOptions.value = true
    currentOptions.value = text.options
  } else {
    showOptions.value = false
  }
}

// ==============================================
// 打字机效果
// ==============================================
const typeEffect = () => {
  isTyping.value = true
  displayedText.value = ''
  const fullText = currentText.value.content
  let index = 0

  const timer = setInterval(() => {
    if (index < fullText.length && isTyping.value) {
      displayedText.value += fullText.charAt(index)
      if (index % 3 === 0) playSFX('type')
      index++
    } else {
      clearInterval(timer)
      isTyping.value = false

      if (autoPlay.value && !showOptions.value) {
        setTimeout(nextLine, 2000)
      }
    }
  }, 40)
}

// ==============================================
// 点击交互
// ==============================================
const handleInteraction = () => {
  if (!isAudioInitialized.value) {
    isAudioInitialized.value = true
    playSFX('click')
    loadScene()
    typeEffect()
    return
  }

  if (isTyping.value) {
    displayedText.value = currentText.value.content
    isTyping.value = false
    return
  }

  if (showOptions.value) return
  nextLine()
}

// 下一句
function nextLine() {
  if (currentStep.value < currentScript.value.length - 1) {
    currentStep.value++
    playSFX('click')
    loadScene()
    typeEffect()
    if (currentText.value.sfx) playSFX(currentText.value.sfx)
  }
}

// 选择选项
const selectOption = (nextStep) => {
  showOptions.value = false
  currentStep.value = nextStep
  playSFX('click')
  loadScene()
  typeEffect()
}

// ==============================================
// 自动播放
// ==============================================
const toggleAuto = () => {
  autoPlay.value = !autoPlay.value
}

// ==============================================
// 快速存档 / 读档
// ==============================================
const quickSave = () => {
  const saveData = {
    chapter: currentChapter.value,
    step: currentStep.value
  }
  localStorage.setItem('vn_save', JSON.stringify(saveData))
  alert('✅ 快速存档成功')
}

const quickLoad = () => {
  const data = localStorage.getItem('vn_save')
  if (!data) return alert('❌ 无存档')
  const parse = JSON.parse(data)
  currentChapter.value = parse.chapter
  currentStep.value = parse.step
  loadScene()
  typeEffect()
  alert('✅ 快速读档成功')
}

const toggleMute = () => {
  isMuted.value = !isMuted.value
}
</script>

<style scoped lang="scss">
$gold: #c9a063;
$gold-light: #e2c088;
$black: #0a0a0d;

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.vn-container {
  width: 100vw;
  height: 100vh;
  background: $black;
  color: #fff;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  font-family: 'Inter', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  user-select: none;
}

.vn-background {
  position: absolute;
  inset: 0;
  z-index: 1;

  .bg-img {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.4;
  }

  .moon-light {
    position: absolute;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 75% 8%, rgba(255, 255, 255, 0.08) 0%, transparent 55%);
  }

  .vignette {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at center, transparent 40%, rgba(0, 0, 0, 0.7) 100%);
  }
}

.characters {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.char {
  position: absolute;
  bottom: 18%;
  height: 75vh;
  display: flex;
  align-items: flex-end;
  transition: all 0.5s ease;

  img {
    height: 100%;
    object-fit: cover;
    opacity: 0.85;
    filter: brightness(0.85);
  }

  &.char-left {
    left: 10%;
  }

  &.char-right {
    right: 10%;
    flex-direction: row-reverse;
  }

  &.char-active img {
    filter: brightness(1.1);
    transform: scale(1.03);
  }
}

.audio-gate {
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;

  .gate-content {
    text-align: center;
    animation: fadeIn 1s ease;

    .icon {
      font-size: 44px;
      margin-bottom: 24px;
      animation: pulse 2s infinite;
    }

    p {
      letter-spacing: 2px;
      color: $gold;
      font-size: 15px;
    }
  }
}

.vn-main {
  position: relative;
  z-index: 10;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0 8% 12% 8%;

  .chapter-title {
    position: absolute;
    top: 8%;
    left: 50%;
    transform: translateX(-50%);
    color: $gold-light;
    font-size: 16px;
    letter-spacing: 3px;
    display: flex;
    align-items: center;
    gap: 20px;
    white-space: nowrap;
    opacity: 0.8;

    .line {
      width: 60px;
      height: 1px;
      background: linear-gradient(90deg, transparent, $gold, transparent);
    }
  }
}

.scene-hint {
  position: absolute;
  top: 16%;
  right: 8%;
  color: $gold;
  font-family: monospace;
  font-size: 14px;
  padding: 6px 0 6px 18px;
  border-left: 2px solid $gold;
  animation: fadeIn 0.6s ease;
  letter-spacing: 1px;
}

.dialogue-wrapper {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

.dialogue-box {
  background: rgba(12, 12, 16, 0.88);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 60px rgba(201, 160, 99, 0.08);
  padding: 48px 52px;
  border-radius: 8px;
  position: relative;
  animation: boxBreath 4s ease-in-out infinite alternate;

  .char-name {
    position: absolute;
    top: -14px;
    left: 32px;
    background: $gold;
    color: #000;
    padding: 3px 16px;
    font-size: 13px;
    font-weight: bold;
    border-radius: 2px;
    letter-spacing: 1px;
  }

  .text-content {
    font-size: 18px;
    line-height: 1.9;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.9);
    min-height: 120px;
  }

  .cursor {
    color: $gold;
    animation: blink 1s steps(1) infinite;
    font-weight: bold;
    margin-left: 2px;
  }

  .next-indicator {
    position: absolute;
    right: 32px;
    bottom: 24px;
    width: 10px;
    height: 10px;
    border-right: 2px solid $gold;
    border-bottom: 2px solid $gold;
    transform: rotate(45deg);
    animation: nextBlink 1.8s infinite;
  }
}

.options-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 600px;
  margin: 20px auto 0;
  z-index: 11;
}

.option-btn {
  background: rgba(20, 20, 28, 0.85);
  border: 1px solid rgba(201, 160, 99, 0.4);
  color: #fff;
  padding: 16px 24px;
  border-radius: 6px;
  font-size: 16px;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: rgba(201, 160, 99, 0.15);
    border-color: $gold;
    transform: translateX(6px);
  }
}

.top-controls {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  gap: 20px;
  font-size: 12px;
  color: $gold;
  font-family: monospace;

  span {
    cursor: pointer;
    padding: 4px 10px;
    border: 1px solid rgba(201, 160, 99, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(201, 160, 99, 0.1);
    }
  }
}

.ui-overlay {
  position: absolute;
  bottom: 36px;
  width: 100%;
  padding: 0 8%;
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #555;
  font-family: monospace;
  z-index: 20;

  .controls span {
    margin-left: 20px;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $gold;
    }
  }
}

.shake {
  animation: shake 0.5s cubic-bezier(.36, .07, .19, .97) both;
}

.fade-char-enter-active {
  transition: all 0.6s ease;
}

.fade-char-leave-active {
  transition: all 0.4s ease;
}

.fade-char-enter-from {
  opacity: 0;
  transform: translateY(40px);
}

.fade-char-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-options-enter-active {
  transition: all 0.4s ease;
}

.fade-options-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes boxBreath {
  from {
    box-shadow: 0 0 50px rgba(201, 160, 99, 0.06);
  }

  to {
    box-shadow: 0 0 70px rgba(201, 160, 99, 0.12);
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.05);
  }
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

@keyframes nextBlink {

  0%,
  100% {
    opacity: 0.3;
  }

  50% {
    opacity: 1;
  }
}

@keyframes shake {

  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-3px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(3px, 0, 0);
  }
}

.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-leave-to {
  opacity: 0;
}
</style>